import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  FileTextOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Timeline, Spin, Empty, Button, Tag } from 'antd';
import { formatDate, DATE_FORMATS } from '@/utils/formUtils';
import {
  groupHistoryChanges,
  applyHistoryFilters,
  DEFAULT_HISTORY_FILTERS,
  EMPTY_DISPLAY,
} from '@/shared/history';
import { getUserFullName } from '@/utils/utils';
import HistoryFilters from './HistoryFilters';
import SnapshotFields from './SnapshotFields';

/** Entries rendered before the "load more" button appears. */
const PAGE_SIZE = 20;

/**
 * One `label: before → after` line.
 */
const ChangeLine = ({ line }) => (
  <div className="flex flex-wrap items-baseline gap-x-1.5 text-sm leading-6">
    <span className="font-medium text-gray-700">
      {line.label}:
    </span>
    <span className="text-gray-500 line-through decoration-gray-400">
      {line.from}
    </span>
    <span className="text-gray-400">→</span>
    <span className="font-medium text-gray-900">{line.to}</span>
  </div>
);

/**
 * An array whose elements changed, shown one block per element.
 */
const ArrayGroup = ({ group }) => (
  <div className="mt-1">
    <div className="text-sm font-medium text-gray-700">
      {group.label}
    </div>
    <ul className="mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
      {group.items.map((item) => (
        <li key={`${group.id}-${item.index}`}>
          <span className="text-sm font-medium text-gray-600">
            {item.label}
          </span>
          <div className="pl-3">
            {item.lines.map((line) => (
              <ChangeLine key={line.id} line={line} />
            ))}
          </div>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Everything that changed in one entry, grouped into sections.
 */
const ChangeDetails = ({ grouped }) => (
  <div className="mt-2 space-y-3 rounded border border-gray-200 bg-gray-50 p-3">
    {grouped.sections.map((section) => (
      <div key={section.key}>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {section.label}
        </div>

        {section.lines.map((line) => (
          <ChangeLine key={line.id} line={line} />
        ))}

        {section.arrays.map((group) => (
          <ArrayGroup key={group.id} group={group} />
        ))}

        {section.references.map((group) => (
          <div key={group.id} className="mt-1">
            <div className="text-sm font-medium text-gray-700">
              {group.label}
            </div>
            <div className="border-l-2 border-gray-200 pl-3">
              {group.lines.map((line) => (
                <ChangeLine key={line.id} line={line} />
              ))}
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * One change entry: who, when, a one-glance summary, and details on demand.
 */
const ChangeEntry = ({ event, modelName, t, locale }) => {
  const [expanded, setExpanded] = useState(false);

  const grouped = useMemo(
    () => groupHistoryChanges(event.changes, modelName, { t, locale }),
    [event.changes, modelName, t, locale]
  );

  const headline = grouped.headline
    ? t('ui.recordChanged', { section: grouped.headline.label })
    : t('ui.changed');

  if (!grouped.totalLines) {
    return (
      <div className="text-sm text-gray-400">{t('ui.noChangeLines')}</div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-800">
          {headline} — {t('ui.fieldCount', { count: grouped.totalLines })}
        </span>
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t('ui.hideDetails') : t('ui.showDetails')}
        </Button>
      </div>

      {!expanded && (
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-sm text-gray-600">
          {grouped.summary.map((line, index) => (
            <span key={line.id} className="whitespace-nowrap">
              {index > 0 && <span className="mr-2 text-gray-300">·</span>}
              {line.label}: {line.from} → {line.to}
            </span>
          ))}
          {grouped.summaryArrays.map((group) => (
            <span key={group.id} className="whitespace-nowrap">
              {grouped.summary.length > 0 && (
                <span className="mr-2 text-gray-300">·</span>
              )}
              {group.label}: {group.summary}
            </span>
          ))}
        </div>
      )}

      {expanded && <ChangeDetails grouped={grouped} />}
    </div>
  );
};

/**
 * A full-state capture. Shown only to roles allowed to see snapshots.
 */
const SnapshotEntry = ({ event, modelName, t, locale }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-800">
          {t('ui.snapshotTaken')}
        </span>
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t('ui.hideSnapshot') : t('ui.viewSnapshot')}
        </Button>
      </div>

      {expanded && (
        <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-3">
          <SnapshotFields
            data={event.data}
            modelName={modelName}
            t={t}
            locale={locale}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Profile page: one entry as a card — the author's initials on the left (a
 * camera for snapshots), name and time on top, the change below.
 */
const ProfileEntry = ({ event, last, modelName, t, locale }) => {
  const isSnapshot = event.type === 'snapshot';
  const userName = event.user
    ? getUserFullName(event.user.firstName, event.user.lastName)
    : t('ui.system');
  const initials = event.user
    ? `${event.user.firstName?.[0] || ''}${event.user.lastName?.[0] || ''}`.toUpperCase()
    : '·';

  return (
    <li className="relative flex gap-3">
      {!last && (
        <span className="absolute bottom-[-12px] left-[15px] top-9 w-0.5 rounded bg-zinc-200" />
      )}
      <span
        className={
          isSnapshot
            ? 'z-[1] grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-100 text-sky-600'
            : 'z-[1] grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-[11px] font-bold text-white'
        }
      >
        {isSnapshot ? <FileTextOutlined /> : initials}
      </span>
      <div className="min-w-0 flex-1 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="flex items-center gap-2">
            <span className="font-bold text-zinc-800">{userName}</span>
            {isSnapshot && <Tag color="blue">{t('ui.snapshot')}</Tag>}
          </span>
          <span className="text-zinc-400">
            {formatDate(event.timestamp, DATE_FORMATS.DISPLAY_DOTS) ??
              EMPTY_DISPLAY}{' '}
            {formatDate(event.timestamp, DATE_FORMATS.TIME_ONLY) ?? ''}
          </span>
        </div>
        {isSnapshot ? (
          <SnapshotEntry
            event={event}
            modelName={modelName}
            t={t}
            locale={locale}
          />
        ) : (
          <ChangeEntry
            event={event}
            modelName={modelName}
            t={t}
            locale={locale}
          />
        )}
      </div>
    </li>
  );
};

/**
 * TimelineTab
 *
 * A chronological, readable account of what happened to one record: who changed
 * it, when, and what moved — summarised to a line or two, with the full detail a
 * click away.
 */
export default function TimelineTab({
  data,
  loading,
  modelName,
  canViewSnapshots,
  // 'profile': each entry as a card with the author's initials.
  variant,
}) {
  const { t, i18n } = useTranslation('history');
  const locale = i18n.language || 'sr';

  const [filters, setFilters] = useState({ ...DEFAULT_HISTORY_FILTERS });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const events = useMemo(() => {
    const source = canViewSnapshots
      ? data || []
      : (data || []).filter((event) => event.type !== 'snapshot');
    return source;
  }, [data, canViewSnapshots]);

  const filtered = useMemo(
    () => applyHistoryFilters(events, filters, modelName, { t, locale }),
    [events, filters, modelName, t, locale]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!events.length) {
    return <Empty description={t('ui.noHistory')} className="my-8" />;
  }

  const visible = filtered.slice(0, visibleCount);

  const timelineItems = visible.map((event) => {
    const isSnapshot = event.type === 'snapshot';
    const userName = event.user
      ? getUserFullName(event.user.firstName, event.user.lastName)
      : t('ui.system');

    return {
      key: event.id,
      dot: isSnapshot ? (
        <FileTextOutlined style={{ fontSize: '14px' }} />
      ) : (
        <ClockCircleOutlined style={{ fontSize: '14px' }} />
      ),
      color: isSnapshot ? 'blue' : 'green',
      children: (
        <div className="pb-4">
          <div className="mb-1 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
            <span className="font-medium text-gray-700">
              {formatDate(event.timestamp, DATE_FORMATS.DISPLAY_DOTS) ??
                EMPTY_DISPLAY}{' '}
              {formatDate(event.timestamp, DATE_FORMATS.TIME_ONLY) ?? ''}
            </span>
            <span>{userName}</span>
            {isSnapshot && <Tag color="blue">{t('ui.snapshot')}</Tag>}
          </div>

          {isSnapshot ? (
            <SnapshotEntry
              event={event}
              modelName={modelName}
              t={t}
              locale={locale}
            />
          ) : (
            <ChangeEntry
              event={event}
              modelName={modelName}
              t={t}
              locale={locale}
            />
          )}
        </div>
      ),
    };
  });

  return (
    <div className="p-2">
      <HistoryFilters
        events={events}
        filters={filters}
        onChange={(next) => {
          setFilters(next);
          setVisibleCount(PAGE_SIZE);
        }}
        t={t}
      />

      {!filtered.length ? (
        <Empty description={t('ui.noResults')} className="my-8" />
      ) : (
        <>
          {variant === 'profile' ? (
            <ol className="mt-4 grid gap-3">
              {visible.map((event, index) => (
                <ProfileEntry
                  key={event.id}
                  event={event}
                  last={index === visible.length - 1}
                  modelName={modelName}
                  t={t}
                  locale={locale}
                />
              ))}
            </ol>
          ) : (
            <Timeline items={timelineItems} className="mt-4" />
          )}

          <div className="flex items-center justify-center gap-3 pb-2 text-xs text-gray-500">
            <span>
              {t('ui.showing', {
                shown: visible.length,
                total: filtered.length,
              })}
            </span>
            {visible.length < filtered.length && (
              <Button
                size="small"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                {t('ui.loadMore')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
